/* eslint-disable react/jsx-props-no-spreading */
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    FormLabel,
    Stack,
} from "@mui/material"
import { ReactElement, ReactNode } from "react"
import {
    Controller,
    ControllerRenderProps,
    FieldValues,
    Control,
} from "react-hook-form"

import {
    parseReactHookFormRadioString,
    objectOnCondition,
} from "../../../utils/general/generalUtils"

interface IValidatedCheckBoxFormGroupProps<T extends string> {
    field: ControllerRenderProps<FieldValues, T>
    children: ReactNode
}

/**
 * Abstracts the logic for a checkbox form group. This is used by the
 * ValidatedCheckBox component.
 */
export const ValidatedCheckBoxFormGroup = <T extends string>({
    field,
    children,
}: IValidatedCheckBoxFormGroupProps<T>): ReactElement => {
    const fieldValue = field.value ?? []
    return (
        <FormGroup
            {...field}
            onChange={(e) => {
                const target = e.target as HTMLInputElement
                const parsedValue = parseReactHookFormRadioString(target.value)
                // If the checkbox is checked, add the value to the array
                if (target.checked) {
                    field.onChange([...fieldValue, parsedValue])
                    // Else, remove it
                } else {
                    field.onChange(
                        fieldValue.filter(
                            (item: string) => item !== parsedValue
                        )
                    )
                }
            }}
        >
            {children}
        </FormGroup>
    )
}

type TCheckboxOptions = {
    [key: string]: string
}

interface IValidatedCheckBoxProps {
    control: Control
    registerName: string
    label: string
    required: boolean
    checkboxOptions: TCheckboxOptions
    customRules?: any // todo
    horizontalMode?: boolean
    disabled?: boolean
}

/*
 * Checkbox with designed to be controlled by react-hook-form. Must be used
 * in a component with a useForm hook from react-hook-form. Register name
 * and control are exposed from useForm. The form value is an array of strings
 * corresponding to the selected checkboxes.
 */
const ValidatedCheckBox = ({
    control,
    registerName,
    label,
    required,
    checkboxOptions,
    customRules,
    horizontalMode,
    disabled,
}: IValidatedCheckBoxProps): ReactElement => {
    const requiredRule = {
        required: { value: true, message: "This input is required" },
    }

    const rules = required ? { ...requiredRule, ...customRules } : customRules
    return (
        <Controller
            rules={rules}
            control={control}
            name={registerName}
            render={({ field, fieldState: { error } }) => (
                <FormControl error={!!error} variant="standard">
                    <Stack
                        direction={horizontalMode ? "row" : "column"}
                        spacing={horizontalMode ? 4 : 0}
                    >
                        <FormLabel
                            required={required}
                            sx={{
                                ...objectOnCondition(!!horizontalMode, {
                                    display: "flex",
                                    alignItems: "center",
                                }),
                            }}
                        >
                            {label}
                        </FormLabel>
                        <ValidatedCheckBoxFormGroup field={field}>
                            {Object.entries(checkboxOptions).map(
                                ([labelOfValue, value]) => (
                                    <FormControlLabel
                                        key={value}
                                        value={value}
                                        control={
                                            <Checkbox
                                                checked={(
                                                    field?.value ?? []
                                                ).includes(value)}
                                                disabled={disabled}
                                            />
                                        }
                                        label={labelOfValue}
                                    />
                                )
                            )}
                        </ValidatedCheckBoxFormGroup>
                    </Stack>

                    {!!error && (
                        <FormHelperText error={!!error}>
                            {error?.message}
                        </FormHelperText>
                    )}
                </FormControl>
            )}
        />
    )
}

ValidatedCheckBox.defaultProps = {
    customRules: {},
    horizontalMode: false,
    disabled: false,
}
export default ValidatedCheckBox
